/**
 * VideoPreview - Modal for previewing and sharing recorded video
 * 
 * Features:
 * - Customizable share button (render prop or default)
 * - Built-in Web Share API support with file sharing
 * - Works for both webar and webxr contexts
 * 
 * Requires Tailwind CSS.
 */

import { useRef, useEffect, useState, useCallback, ReactNode } from 'react';
import emitter from '@webcore/core/events/eventemitter';

export interface VideoPreviewProps {
  /** Video URL to preview */
  videoUrl: string | null;
  /** Called when modal is closed */
  onClose: () => void;
  /** Custom share handler (if not provided, uses Web Share API) */
  onShare?: () => void;
  /** Title for share data (default: 'AR Experience') */
  shareTitle?: string;
  /** Text for share data */
  shareText?: string;
  /** Hide the share button entirely */
  hideShareButton?: boolean;
  /** Filename for shared file (default: 'ar-recording.mp4') */
  fileName?: string;
  /** Custom share button renderer */
  renderShareButton?: (handleShare: () => void) => ReactNode;
  /** Custom close button renderer */
  renderCloseButton?: (handleClose: () => void) => ReactNode;
  /** Custom buttons renderer */
  renderButtons?: (handlers: { share: () => void }) => ReactNode;
  /** Custom className for the overlay */
  className?: string;
}

export function VideoPreview({
  videoUrl,
  onClose,
  onShare,
  shareTitle = 'AR Experience',
  shareText = 'Check out this AR experience!',
  hideShareButton = false,
  fileName = 'ar-recording.mp4',
  renderShareButton,
  renderCloseButton,
  renderButtons,
  className = '',
}: VideoPreviewProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoLoaded, setVideoLoaded] = useState(false);

  useEffect(() => {
    if (videoRef.current && videoUrl) {
      videoRef.current.src = videoUrl;
      videoRef.current.load();
      videoRef.current.play().catch(err => console.error('Error playing video:', err));
    }
  }, [videoUrl]);

  // Share handler - tries Web Share API first, falls back to emitter for iOS WebXR
  const handleShare = useCallback(async () => {
    if (onShare) {
      onShare();
      return;
    }
    
    if (!videoUrl) return;

    // Try Web Share API first
    if (navigator.share) {
      try {
        // Try file sharing first
        const response = await fetch(videoUrl);
        const blob = await response.blob();
        const file = new File([blob], fileName, { type: 'video/mp4' });
        
        await navigator.share({
          title: shareTitle,
          text: shareText,
          files: [file],
        });
        return;
      } catch (fileShareError) {
        // File sharing failed, try URL sharing
        try {
          await navigator.share({
            title: shareTitle,
            text: shareText,
            url: videoUrl,
          });
          return;
        } catch (urlShareError) {
          // console.log('Web Share API failed');
        }
      }
    }
    
    // Emit share event (for App Clips)
    emitter.emit('share', {
      title: shareTitle,
      text: shareText,
      url: videoUrl,
    });
  }, [videoUrl, shareTitle, shareText, fileName, onShare]);

  if (!videoUrl) return null;

  return (
    <div 
      className={`fixed inset-0 z-[200] bg-black/40 flex flex-col items-center justify-center p-4 ${className}`}
      style={{ pointerEvents: 'auto' }}
    >
      {/* Video container */}
      <div className="relative w-full max-w-[70vw] rounded-[22px] overflow-hidden border-[3px] border-white">
        {/* Loading placeholder */}
        {!videoLoaded && (
          <div className="absolute inset-0 bg-neutral-800 animate-pulse flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        
        <video
          ref={videoRef}
          className="w-full min-h-[50vh] object-cover block"
          playsInline
          autoPlay
          muted
          loop
          onLoadedData={() => setVideoLoaded(true)}
        />
        
        {/* Close button */}
        {renderCloseButton ? (
          renderCloseButton(onClose)
        ) : (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-[50px] h-[50px] p-0 bg-transparent border-none cursor-pointer z-10"
            aria-label="Close video preview"
          >
            <svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="25" cy="25" r="25" fill="black" fillOpacity="0.5" />
              <circle cx="25" cy="25" r="23.5" stroke="white" strokeWidth="2" />
              <path
                d="M22.9287 25.0055L16.4062 18.4789L18.4886 16.4062L25 22.9218L31.5224 16.4062L33.5937 18.4789L27.0823 25.0055L33.5937 31.5211L31.5224 33.5937L25 27.0782L18.4886 33.5937L16.4173 31.5211L22.9287 25.0055Z"
                fill="white"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Buttons */}
      {renderButtons ? (
        renderButtons({ share: handleShare })
      ) : (
        <div className="flex flex-col gap-3 mt-6 w-full max-w-[290px]">
          {/* Share button - matches Figma design */}
          {!hideShareButton && (
            renderShareButton ? (
              renderShareButton(handleShare)
            ) : (
              <button
                onClick={handleShare}
                className="w-full h-[56px] rounded-none border-2 border-white text-white font-bold text-lg tracking-wide cursor-pointer active:opacity-70 transition-opacity"
                style={{
                  background: 'rgba(0, 0, 0, 0.05)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  boxShadow: '0px 4px 40px rgba(0, 0, 0, 0.1)',
                }}
              >
                Share
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
}

export default VideoPreview;
